import { Link, useSearchParams } from "react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePublicPositions } from "@/api/hooks/public";
import type { PositionPublic } from "@/api/endpoints";
import { fmtSalary, humanize, relTime } from "@/lib/format";
import { buttonClass } from "@/components/ui/Button";

const SENIORITIES: Array<{ value: string; label: string }> = [
  { value: "junior", label: "Junior" },
  { value: "middle", label: "Middle" },
  { value: "senior", label: "Senior" },
  { value: "lead", label: "Lead / Principal" },
];

const ENGAGEMENTS = [
  { value: "outstaffing", label: "Outstaffing" },
  { value: "recruiting", label: "Direct hire (recruiting)" },
];

const COMMON_SKILLS = ["Go", "Python", "TypeScript", "React", "Java", "Kubernetes"];

const POSTED_OPTS: Array<{ value: "any" | "24h" | "week" | "month"; label: string }> = [
  { value: "any", label: "Any time" },
  { value: "24h", label: "Past 24 hours" },
  { value: "week", label: "Past week" },
  { value: "month", label: "Past month" },
];

function readArray(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).filter(Boolean);
}

export function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  // Mirror URL → state. URL is the source of truth so deep links work.
  const filters = useMemo(
    () => ({
      q: searchParams.get("q") ?? "",
      seniority: readArray(searchParams, "seniority"),
      engagement_type: readArray(searchParams, "engagement_type"),
      stack: readArray(searchParams, "stack"),
      min_rate: numericOrUndef(searchParams.get("min_rate")),
      max_rate: numericOrUndef(searchParams.get("max_rate")),
      posted: (searchParams.get("posted") as "any" | "24h" | "week" | "month") ?? "any",
      sort: (searchParams.get("sort") as "newest" | "salary_desc") ?? "newest",
      page: parseInt(searchParams.get("page") ?? "1", 10),
    }),
    [searchParams],
  );

  const PAGE_SIZE = 20;

  const { data, isLoading, error } = usePublicPositions({
    q: filters.q || undefined,
    seniority: filters.seniority.length ? filters.seniority : undefined,
    engagement_type: filters.engagement_type.length ? filters.engagement_type : undefined,
    stack: filters.stack.length ? filters.stack : undefined,
    min_rate: filters.min_rate,
    max_rate: filters.max_rate,
    posted: filters.posted,
    sort: filters.sort,
    page: filters.page,
    page_size: PAGE_SIZE,
  });

  function update(patch: Record<string, string | string[] | number | undefined>, resetPage = true) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k);
      if (Array.isArray(v)) v.forEach((item) => item && next.append(k, item));
      else if (v != null && v !== "") next.set(k, String(v));
    }
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
  }

  function toggleArray(key: "seniority" | "engagement_type" | "stack", value: string) {
    const cur = filters[key];
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    update({ [key]: next });
  }

  // Debounced search input.
  const [searchDraft, setSearchDraft] = useState(filters.q);
  useEffect(() => setSearchDraft(filters.q), [filters.q]);
  const debounceRef = useRef<number | null>(null);
  function onSearch(v: string) {
    setSearchDraft(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => update({ q: v || undefined }), 300);
  }

  function clearAll() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 sm:py-12">
      <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Find your next role</h1>
          <p className="mt-1 text-sm text-text-muted">
            {isLoading
              ? "Loading…"
              : error
                ? "Something went wrong"
                : `${total} open position${total === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <Link
            to="/openapplication"
            className={`${buttonClass({ variant: "secondary" })} w-full sm:w-auto`}
          >
            Open application
          </Link>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`${buttonClass({ variant: "ghost" })} w-full sm:hidden`}
            aria-expanded={showFilters}
          >
            {showFilters ? "Hide filters" : "Show filters"}
          </button>
          <div className="flex w-full items-center gap-2.5 rounded-xl border border-border bg-surface px-3.5 py-2.5 sm:min-w-[320px] sm:max-w-[420px]">
            <SearchIcon />
            <input
              type="text"
              value={searchDraft}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by title, skill or company…"
              className="flex-1 border-none bg-transparent text-sm text-text outline-none placeholder:text-text-soft"
            />
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[280px_1fr]">
        {/* Filters */}
        <aside
          className={`${showFilters ? "block" : "hidden"} h-fit rounded-2xl border border-border bg-surface p-6 md:sticky md:top-[88px] md:block`}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Filters</h2>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-primary hover:underline"
            >
              Clear all
            </button>
          </div>

          <FilterGroup title="Seniority">
            {SENIORITIES.map((s) => (
              <Check
                key={s.value}
                label={s.label}
                checked={filters.seniority.includes(s.value)}
                onChange={() => toggleArray("seniority", s.value)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Engagement model">
            {ENGAGEMENTS.map((e) => (
              <Check
                key={e.value}
                label={e.label}
                checked={filters.engagement_type.includes(e.value)}
                onChange={() => toggleArray("engagement_type", e.value)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Stack">
            {COMMON_SKILLS.map((s) => (
              <Check
                key={s}
                label={s}
                checked={filters.stack.includes(s)}
                onChange={() => toggleArray("stack", s)}
              />
            ))}
          </FilterGroup>

          <FilterGroup title="Monthly rate (USD)">
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder="Min"
                value={filters.min_rate ?? ""}
                onChange={(e) => update({ min_rate: e.target.value || undefined })}
                className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
              />
              <span className="text-text-muted">–</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.max_rate ?? ""}
                onChange={(e) => update({ max_rate: e.target.value || undefined })}
                className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
              />
            </div>
          </FilterGroup>

          <FilterGroup title="Posted" last>
            {POSTED_OPTS.map((opt) => (
              <label key={opt.value} className="flex cursor-pointer items-center gap-2.5 py-1 text-sm text-text">
                <input
                  type="radio"
                  name="posted"
                  checked={filters.posted === opt.value}
                  onChange={() => update({ posted: opt.value })}
                  className="size-4 accent-primary"
                />
                {opt.label}
              </label>
            ))}
          </FilterGroup>
        </aside>

        {/* Results */}
        <div>
          <div className="mb-3 flex justify-start md:justify-end">
            <select
              value={filters.sort}
              onChange={(e) => update({ sort: e.target.value })}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-[13px] text-text outline-none"
            >
              <option value="newest">Sort: Newest</option>
              <option value="salary_desc">Sort: Salary high → low</option>
            </select>
          </div>

          {isLoading ? (
            <Placeholder>Loading jobs…</Placeholder>
          ) : error ? (
            <Placeholder error>Could not load positions. Try again.</Placeholder>
          ) : !data?.items.length ? (
            <Placeholder>No matching positions. Try adjusting your filters.</Placeholder>
          ) : (
            <div className="flex flex-col gap-3.5">
              {data.items.map((p) => (
                <JobCard key={p.id} position={p} />
              ))}
            </div>
          )}

          {lastPage > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Pager
                disabled={filters.page <= 1}
                onClick={() => update({ page: filters.page - 1 }, false)}
              >
                ‹ Prev
              </Pager>
              {pageRange(filters.page, lastPage).map((n, i) =>
                n === "…" ? (
                  <span key={`gap-${i}`} className="px-2 text-text-muted">
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    onClick={() => update({ page: n }, false)}
                    className={`min-w-9 rounded-md px-3 py-1.5 text-[13px] ${
                      n === filters.page
                        ? "bg-primary text-white"
                        : "border border-border bg-surface text-text hover:bg-surface-alt"
                    }`}
                  >
                    {n}
                  </button>
                ),
              )}
              <Pager
                disabled={filters.page >= lastPage}
                onClick={() => update({ page: filters.page + 1 }, false)}
              >
                Next ›
              </Pager>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({
  title,
  children,
  last,
}: {
  title: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className={last ? "" : "mb-5"}>
      <h3 className="mb-2.5 text-[12px] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 py-1 text-sm text-text">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-4 accent-primary"
      />
      {label}
    </label>
  );
}

function Pager({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={buttonClass({ variant: "secondary", size: "sm" })}
    >
      {children}
    </button>
  );
}

function Placeholder({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-10 text-center text-sm ${
        error
          ? "border-[#fecaca] bg-danger-soft text-[#b91c1c]"
          : "border-border bg-surface text-text-muted"
      }`}
    >
      {children}
    </div>
  );
}

function JobCard({ position: p }: { position: PositionPublic }) {
  const initial = ((p.company_name || p.title || "•").trim()[0] ?? "•").toUpperCase();
  const skills = p.skills ?? [];
  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-5 transition-all hover:border-primary hover:shadow-md sm:flex-row sm:items-start sm:gap-4.5">
      <div
        className="grid size-12 shrink-0 place-items-center rounded-xl text-lg font-bold text-white"
        style={{
          background: `linear-gradient(135deg, ${gradient(p.id)})`,
        }}
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-semibold">{p.title}</h3>
        <p className="mb-2.5 text-sm text-text-muted">
          {p.company_name}
          {p.location ? ` · ${p.location}` : ""}
        </p>
        <div className="mb-2.5 flex flex-wrap gap-3.5 text-[13px] text-text-muted">
          {p.engagement_type && (
            <span>
              {humanize(p.engagement_type, "service")}
              {p.schedule ? ` · ${humanize(p.schedule, "engagement")}` : ""}
            </span>
          )}
          {p.seniority && <span>{humanize(p.seniority, "seniority")}</span>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {skills.map((s) => (
            <span
              key={s}
              className="rounded-full bg-surface-alt px-2.5 py-0.5 text-xs font-medium text-text"
            >
              {s}
            </span>
          ))}
        </div>
      </div>
      <div className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2.5 sm:flex-col sm:items-end">
        <div className="text-[15px] font-bold text-text">
          {fmtSalary(p.salary_min, p.salary_max, p.currency)}
        </div>
        <div className="text-xs text-text-soft">{relTime(p.posted_at)}</div>
        <Link
          to={`/jobs/${p.id}/apply`}
          className={`${buttonClass({ size: "sm" })} w-full justify-center sm:w-auto`}
        >
          Apply
        </Link>
      </div>
    </article>
  );
}

const PALETTE = [
  ["#FBBF24", "#F87171"],
  ["#5B8DEF", "#A78BFA"],
  ["#4ADE80", "#5B8DEF"],
  ["#A78BFA", "#5B8DEF"],
  ["#F87171", "#FBBF24"],
  ["#34D399", "#A78BFA"],
];
function gradient(seed: number): string {
  const [a, b] = PALETTE[seed % PALETTE.length];
  return `${a}, ${b}`;
}

function pageRange(current: number, last: number): Array<number | "…"> {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const out: Array<number | "…"> = [1];
  if (current > 3) out.push("…");
  for (let i = Math.max(2, current - 1); i <= Math.min(last - 1, current + 1); i++) {
    out.push(i);
  }
  if (current < last - 2) out.push("…");
  out.push(last);
  return out;
}

function numericOrUndef(s: string | null): number | undefined {
  if (!s) return undefined;
  const n = parseInt(s, 10);
  return isNaN(n) ? undefined : n;
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
